import { defineStore } from 'pinia'
import { errorMessage } from '@/services/http'
import { materialService } from '@/services/materials'
import type { UserMaterial, UserMaterialPayload } from '@/types/api'

interface MaterialsState {
  items: UserMaterial[]
  loaded: boolean
  loading: boolean
  saving: boolean
  error: string | null
}

export const useMaterialsStore = defineStore('materials', {
  state: (): MaterialsState => ({
    items: [],
    loaded: false,
    loading: false,
    saving: false,
    error: null,
  }),

  getters: {
    byId: (state) => (id: string): UserMaterial | null =>
      state.items.find((material) => material.id === id) ?? null,
  },

  actions: {
    async refresh(): Promise<void> {
      if (this.loading) return
      this.loading = true
      try {
        this.items = await materialService.list()
        this.loaded = true
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }
    },

    async ensureLoaded(): Promise<void> {
      if (this.loaded) return
      await this.refresh()
    },

    apply(material: UserMaterial): void {
      const index = this.items.findIndex((item) => item.id === material.id)
      if (index === -1) this.items.push(material)
      else this.items[index] = material
    },

    async save(payload: UserMaterialPayload): Promise<UserMaterial | null> {
      this.error = null
      this.saving = true
      try {
        const saved = await materialService.save(payload)
        this.apply(saved)
        return saved
      } catch (e) {
        this.error = errorMessage(e)
        return null
      } finally {
        this.saving = false
      }
    },

    async remove(id: string): Promise<boolean> {
      this.error = null
      this.saving = true
      try {
        await materialService.remove(id)
        this.items = this.items.filter((material) => material.id !== id)
        return true
      } catch (e) {
        this.error = errorMessage(e)
        return false
      } finally {
        this.saving = false
      }
    },
  },
})
